// Pure date-range business rules (ported from legacy `composables/format.js` L669–702).
// No Intl, no Vue, no store.

import type { LooseDate } from './parse'

/** The preceding equal-length comparison window returned by {@link getCompareDateRange}. */
export interface CompareDateRange {
  compareStart: Date
  compareEnd: Date
}

/**
 * Is the given date in the same calendar month (and year) as today?
 * Invalid input yields `false`. (Legacy `isSameMonth`.)
 */
export function isSameMonth(dateString: LooseDate): boolean {
  const inputDate = new Date(dateString)
  if (isNaN(inputDate.getTime())) return false

  const currentDate = new Date()
  return (
    inputDate.getFullYear() === currentDate.getFullYear() &&
    inputDate.getMonth() === currentDate.getMonth()
  )
}

/** Is `createdAt` at least 24 hours in the past? (Legacy `isOlderThan24Hours`.) */
export function isOlderThan24Hours(createdAt: LooseDate): boolean {
  const createdDate = new Date(createdAt)
  const now = new Date()

  const diffMs = now.getTime() - createdDate.getTime()
  const hours24 = 24 * 60 * 60 * 1000

  return diffMs >= hours24
}

/**
 * Business rule: the comparison range is the **preceding equal-length, inclusive**
 * window ending the day before `startDate`. Given an inclusive `[start, end]` span of
 * `dayDiff = round((end - start) / 86_400_000) + 1` days, returns the immediately
 * preceding `dayDiff`-day window. (Legacy `getCompareDateRange`.)
 */
export function getCompareDateRange(startDate: LooseDate, endDate: LooseDate): CompareDateRange {
  const start = new Date(startDate)
  const end = new Date(endDate)

  const dayDiff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1

  const compareEnd = new Date(start)
  compareEnd.setDate(compareEnd.getDate() - 1)

  const compareStart = new Date(compareEnd)
  compareStart.setDate(compareStart.getDate() - (dayDiff - 1))

  return { compareStart, compareEnd }
}
